import { createContext, FunctionComponent, useContext, useEffect } from 'react';

// Components
import Head from 'next/head';
import { PageView } from '@Components/Utils/Lazy/Script';

// Types
import Post from '../[id]/PostDetail';
import { CategoryItem } from '@Components/Categories/Context';

// APIs
import analyticApi from '@Libs/api/cms/analytic';
import { findRelated } from '@Libs/api/cms/posts';

// Utilities
import { openApp } from '@Libs/utils';

// Contexts
import { useHttpContext } from '@Libs/http';
import { useViewDetected } from '@Components/Utils/ViewDetected';

// Config
import host from '@Config/host';

interface ProviderProps {
    post: Post;
}

declare type PostContextType = ProviderProps & {
    relatedPosts?: Post[];
    category?: CategoryItem;
};

const initState: PostContextType = {
    post: null,
};

const PostContext = createContext<PostContextType>(initState);

export const usePostManager = () => useContext(PostContext);

export const Provider: FunctionComponent<ProviderProps> = ({ post, ...props }) => {
    const { cmsHttp } = useHttpContext();
    const { deviceType } = useViewDetected();

    const category = post.category || {};
    const { data: relatedPosts } = findRelated(post.id, 0, 5, post.id);

    openApp(`edoctor://article_detail/${post?.id}`);

    useEffect(() => {
        analyticApi(cmsHttp).tracking(post.id, 'post_view');
    }, [post.id]);

    const providerValue = { post, category, relatedPosts };

    const metadata = post?.metadata;
    const metadataDescription = metadata?.description || post.description;

    return (
        <PostContext.Provider value={providerValue}>
            <Head>
                <meta property="al:ios:url" content={`edoctor://article_detail/${post?.id}`} />
                <meta property="al:android:url" content={`edoctor://article_detail/${post?.id}`} />

                {metadata?.keywords && <meta name="keywords" content={metadata.keywords} />}
                <link rel="canonical" href={`${host.homeDomain}/bai-viet/${post.slug || post.id}`} />
                {metadataDescription && <meta name="description" content={metadataDescription.substr(0, 150)} />}
                {post?.image?.url && <meta property="og:image" content={`${metadata?.image?.url || post.image.url}`} />}
                {post?.image?.url && (
                    <link
                        rel={'preload'}
                        as={'image'}
                        href={`${post.image.url}?width=${deviceType === 'desktop' ? 800 : 500}`}
                    />
                )}
            </Head>

            {props.children}

            <PageView title={`eDoctor: ${metadata?.title || post.title}`} />
        </PostContext.Provider>
    );
};

export function withPostManager<P>(WrappedComponent: FunctionComponent): FunctionComponent<P & ProviderProps> {
    return ({ post, ...props }) => {
        return (
            <Provider post={post}>
                <WrappedComponent {...props} />
            </Provider>
        );
    };
}
